import { useMemo, useState } from 'react';
import {
  BLOCKED_REASON_LABEL,
  type CheckinRecord,
  type EventDay,
  type ExhibitorDayState,
} from '@nt-tokyo/shared';
import { formatJstDateTime, REJECT_REASON_LABEL, type KioskData } from '@/lib/kiosk';
import ConfirmDialog from './ConfirmDialog';
import ExhibitorCard from './ExhibitorCard';

// 管理者メニュー (受付端末のタイトルを 3 秒長押しで開く)。
// 本日の受付数と、この端末で記録した受付の表 (取消つき)。自動では閉じない。
export default function KioskMenu({
  kiosk,
  day,
  byId,
  done,
  total,
  onClose,
  onOpenPending,
  onOpenSettings,
}: {
  kiosk: KioskData;
  day: EventDay;
  byId: Map<string, ExhibitorDayState>;
  done: number;
  total: number;
  onClose: () => void;
  onOpenPending: () => void;
  onOpenSettings: () => void;
}) {
  const [cancelTarget, setCancelTarget] = useState<CheckinRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // その日の受付の記録をすべて出す。受付の表はイベントで 1 つだけで、受付端末も admin のスマホも
  // それを見て更新する窓口なので、端末やアカウントでは絞らない (端末を増やしたときや、
  // 運営が admin のスマホで開いたときも同じ表が見える)。
  const rows = useMemo(
    () =>
      kiosk.records
        .filter((r) => r.eventDay === day)
        .sort((a, b) => (a.recordedAt < b.recordedAt ? 1 : -1)),
    [kiosk.records, day]
  );

  const doCancel = async () => {
    if (!cancelTarget) return;
    setBusy(true);
    setError(null);
    try {
      await kiosk.cancelRecord(cancelTarget.id);
      setCancelTarget(null);
    } catch {
      setError('取り消せませんでした。通信状態を確認して、もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  const cancelExhibitor = cancelTarget ? byId.get(cancelTarget.exhibitorId)?.exhibitor : undefined;

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-center bg-black/45 p-2 sm:p-6">
      <div className="flex w-full max-w-[1120px] flex-col gap-3 overflow-hidden rounded-2xl bg-white p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-[26px] font-extrabold text-[#1d1d1f]">
            本日の受付
            <span className="ml-3 text-[#1f4fd6] tabular-nums">
              {done} / {total}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onOpenPending}
              className="rounded-xl border-2 border-[#1f4fd6] bg-[#1f4fd6] px-5 py-2.5 text-[20px] font-bold text-white"
            >
              未受付の一覧 ›
            </button>
            <button
              type="button"
              onClick={onOpenSettings}
              className="rounded-xl border-2 border-[#d9d9de] px-5 py-2.5 text-[20px] font-bold text-[#6b6b70]"
            >
              端末の設定 ›
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border-2 border-[#d9d9de] px-5 py-2.5 text-[20px] font-bold text-[#1d1d1f]"
            >
              閉じる
            </button>
          </div>
        </div>
        {error && <div className="rounded-lg bg-[#fdecea] px-4 py-2 text-[18px] text-[#b3261e]">{error}</div>}
        {kiosk.rejectedRecords.length > 0 && (
          // サーバが受け付けなかった記録。画面では「受付しました」と出ているので、ここで気づいて対応する
          <div className="rounded-xl border-2 border-[#f0b8b3] bg-[#fff6f5] px-4 py-3">
            <div className="text-[18px] font-extrabold text-[#b3261e]">
              送れなかった記録 {kiosk.rejectedRecords.length} 件（サーバが受け付けませんでした。必要なら未受付の一覧から受付し直してください）
            </div>
            <ul className="mt-1 text-[17px]">
              {kiosk.rejectedRecords
                .slice()
                .reverse()
                .map((r) => {
                  const ex = byId.get(r.exhibitorId)?.exhibitor;
                  return (
                    <li key={r.id} className="py-0.5">
                      {formatJstDateTime(r.recordedAt)}　{ex ? `${ex.applicationNumber} ${ex.boothNumbers.join(', ')} ${ex.exhibitorName}` : '（一覧にない出展者）'}
                      　<b className="text-[#b3261e]">{REJECT_REASON_LABEL[r.rejectReason] ?? r.rejectReason}</b>
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
        <div className="flex-1 overflow-auto">
          {rows.length === 0 ? (
            <div className="py-16 text-center text-[20px] text-[#6b6b70]">本日の記録はまだありません</div>
          ) : (
            <table className="w-full border-collapse text-[22px]">
              <thead>
                <tr className="text-left text-[16px] font-extrabold text-[#6b6b70]">
                  {['受付日時', '出展者番号', '配置番号', '出展者名', '机', '椅子', '電源', ''].map((h) => (
                    <th key={h} className="whitespace-nowrap border-b-2 border-[#d9d9de] px-2 py-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <RecordRow
                    key={r.id}
                    record={r}
                    state={byId.get(r.exhibitorId)}
                    pending={kiosk.pendingIds.has(r.id)}
                    onCancel={() => setCancelTarget(r)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {cancelTarget && cancelExhibitor && (
        <ConfirmDialog
          title="この受付を取り消しますか？"
          confirmLabel="取り消す"
          tone="danger"
          busy={busy}
          onConfirm={doCancel}
          onCancel={() => setCancelTarget(null)}
        >
          <div className="text-[18px] text-[#3a3a40]">{formatJstDateTime(cancelTarget.recordedAt)} の受付</div>
          <ExhibitorCard exhibitor={cancelExhibitor} />
        </ConfirmDialog>
      )}
    </div>
  );
}

function RecordRow({
  record,
  state,
  pending,
  onCancel,
}: {
  record: CheckinRecord;
  state: ExhibitorDayState | undefined;
  pending: boolean;
  onCancel: () => void;
}) {
  const ex = state?.exhibitor;
  const cancelled = !!record.cancelledAt;
  const td = 'border-b border-[#eee] px-2 py-2.5 whitespace-nowrap font-bold';
  const lineThrough = cancelled ? 'text-[#b0b0b6] line-through' : 'text-[#1d1d1f]';

  let action: React.ReactNode = null;
  if (pending) {
    action = <span className="text-[16px] font-bold text-[#a15c00]">送信待ち</span>;
  } else if (cancelled) {
    action = <span className="text-[16px] text-[#9a9aa0]">取消済み</span>;
  } else if (record.kind === 'checkin') {
    action = (
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border-2 border-[#f0b8b3] px-4 py-1.5 text-[18px] font-extrabold text-[#b3261e]"
      >
        取消
      </button>
    );
  }

  const note =
    record.kind === 'blocked'
      ? `要対応：${record.reason ? BLOCKED_REASON_LABEL[record.reason] : ''}`
      : record.kind === 'already'
        ? '受付済み（2回目）'
        : record.kind === 'not_required'
        ? '受付不要（土曜受付済み）'
        : record.kind === 'absent_seen'
          ? '不在確認'
          : null;

  const rowBg = record.kind === 'blocked' ? 'bg-[#fff6f5]' : record.kind === 'checkin' ? '' : 'bg-[#f7f7f9]';

  return (
    <tr className={`${rowBg} ${lineThrough}`}>
      <td className={`${td} font-semibold tabular-nums`}>{formatJstDateTime(record.recordedAt)}</td>
      <td className={`${td} tabular-nums`}>{ex?.applicationNumber ?? ''}</td>
      <td className={`${td} ${cancelled ? '' : 'text-[#1f4fd6]'} font-extrabold`}>{ex?.boothNumbers.join(', ')}</td>
      <td className={`${td} whitespace-normal`}>
        {ex?.exhibitorName}
        {record.source === 'list' && <span className="ml-2 text-[14px] font-normal text-[#6b6b70]">（一覧から）</span>}
      </td>
      {note ? (
        <td colSpan={3} className={`${td} ${record.kind === 'blocked' ? 'text-[#b3261e]' : 'text-[#6b6b70]'} text-[18px]`}>
          {note}
        </td>
      ) : (
        <>
          <td className={td}>{ex ? (ex.tableCount > 0 ? `${ex.tableCount}本` : 'なし') : ''}</td>
          <td className={td}>{ex ? (ex.chairCount > 0 ? `${ex.chairCount}脚` : 'なし') : ''}</td>
          <td className={td}>{ex ? (ex.powerWatts > 0 ? `${ex.powerWatts}VA` : 'なし') : ''}</td>
        </>
      )}
      <td className={`${td} text-right no-underline`} style={{ textDecoration: 'none' }}>
        {action}
      </td>
    </tr>
  );
}

